const normalize = (value) => String(value || '').trim().toLowerCase()

const ELIGIBLE_STATUSES = new Set(['waitlist', 'inquired'])

const isEligibleStatus = (value) => ELIGIBLE_STATUSES.has(normalize(value))

export const TEACHER_WAITLIST_DRIVE_BUCKET = '17:00-18:00'

export const formatTeacherWaitlistAddress = (address = {}) => [
    address?.addressLine1 || address?.StreetAddress || address?.streetAddress || address?.street,
    address?.city || address?.City,
    address?.state || address?.State,
    address?.zipcode || address?.Zip || address?.zip,
].map((value) => String(value || '').trim()).filter(Boolean).join(', ')

const hasUsableCoordinates = (directory) => (
    directory?.lat !== null
    && directory?.lat !== undefined
    && directory?.lat !== ''
    && directory?.lng !== null
    && directory?.lng !== undefined
    && directory?.lng !== ''
    && Number.isFinite(Number(directory.lat))
    && Number.isFinite(Number(directory.lng))
)

export const findTeacherWaitlistDirectoryKey = ({
    directories = [],
    entityType,
    entityId,
    fallbackKey,
} = {}) => {
    const targetId = String(entityId || '').trim()
    const targetKey = normalize(fallbackKey)
    const associationField = entityType === 'teacher' ? 'teacherIds' : 'familyIds'

    const match = (Array.isArray(directories) ? directories : []).find((directory) => {
        if (!hasUsableCoordinates(directory)) return false
        const associatedIds = Array.isArray(directory?.[associationField])
            ? directory[associationField].map((id) => String(id))
            : []
        return (
            (targetId && associatedIds.includes(targetId))
            || (targetKey && normalize(directory?.key) === targetKey)
            || (targetId && normalize(directory?.key) === normalize(targetId))
        )
    })

    return match?.key || ''
}

export const findCachedTeacherWaitlistDriveMinutes = ({
    edges = [],
    schoolId,
    fromKey,
    toKey,
    bucket = TEACHER_WAITLIST_DRIVE_BUCKET,
} = {}) => {
    const match = (Array.isArray(edges) ? edges : []).find((edge) => (
        normalize(edge?.schoolId) === normalize(schoolId)
        && normalize(edge?.todBucket || edge?.bucket) === normalize(bucket)
        && normalize(edge?.origin?.key || edge?.origin || edge?.fromKey || edge?.from) === normalize(fromKey)
        && normalize(edge?.destination?.key || edge?.destination || edge?.toKey || edge?.to) === normalize(toKey)
    ))
    const rawMinutes = match?.minutes
    if (rawMinutes === null || rawMinutes === undefined || rawMinutes === '') return null
    const minutes = Number(rawMinutes)
    return Number.isFinite(minutes) && minutes >= 0 ? minutes : null
}

const getInstrumentLabel = (instrument) => {
    if (!instrument) return ''
    if (typeof instrument === 'string') return instrument.trim()

    const parentInstrument = String(
        instrument.parentInstrument || instrument.ParentInstrument || ''
    ).trim()
    const specificInstrument = String(
        instrument.name
        || instrument.Name
        || instrument.instrument
        || instrument.Instrument
        || ''
    ).trim()

    if (parentInstrument && normalize(parentInstrument) !== 'skill') {
        return parentInstrument
    }
    return specificInstrument || parentInstrument
}

const getInstrumentTokens = (instrument) => {
    if (!instrument) return []
    if (typeof instrument === 'string') return [normalize(instrument)].filter(Boolean)

    const values = [
        instrument.id,
        instrument._id,
        getInstrumentLabel(instrument),
    ]

    return Array.from(new Set(values.map(normalize).filter(Boolean)))
}

const instrumentsMatch = (studentInstrument, teacherTokens) => (
    getInstrumentTokens(studentInstrument).some((token) => teacherTokens.has(token))
)

const getStudentKey = (family, student, index) => String(
    student?.studentId
    || student?._id
    || `${family?.familyId || family?._id || 'family'}-${index}`
)

export const buildTeacherWaitlistRows = ({ families = [], teacherInstruments = [] } = {}) => {
    const teacherTokens = new Set(
        (Array.isArray(teacherInstruments) ? teacherInstruments : [])
            .flatMap(getInstrumentTokens)
    )

    if (teacherTokens.size === 0) return []

    const rows = []

    ;(Array.isArray(families) ? families : []).forEach((family) => {
        const familyStatusIsEligible = isEligibleStatus(family?.familyStatus)

        ;(Array.isArray(family?.students) ? family.students : []).forEach((student, index) => {
            const studentStatusIsEligible = isEligibleStatus(student?.studentStatus)
            if (!familyStatusIsEligible && !studentStatusIsEligible) return

            const matchingInstruments = (Array.isArray(student?.instrumentList)
                ? student.instrumentList
                : [])
                .filter((instrument) => instrumentsMatch(instrument, teacherTokens))
                .map(getInstrumentLabel)
                .filter(Boolean)

            if (matchingInstruments.length === 0) return

            const address = family?.address || family?.Address || {}
            const city = String(address?.city || address?.City || '').trim()
            const state = String(address?.state || address?.State || '').trim()
            const studentStatus = studentStatusIsEligible
                ? String(student.studentStatus).trim()
                : String(family.familyStatus).trim()

            rows.push({
                id: getStudentKey(family, student, index),
                familyId: String(family?.familyId || family?._id || '').trim(),
                familyName: String(family?.familyName || family?.name || '').trim(),
                studentName: String(student?.studentName || student?.name || 'Unnamed student').trim(),
                instrument: Array.from(new Set(matchingInstruments)).join(', '),
                familyAddress: formatTeacherWaitlistAddress(address),
                location: [city, state].filter(Boolean).join(', ') || 'Location not listed',
                status: studentStatus,
                statusScope: studentStatusIsEligible ? 'student' : 'family',
            })
        })
    })

    return rows.sort((left, right) => (
        left.studentName.localeCompare(right.studentName)
        || left.familyName.localeCompare(right.familyName)
    ))
}
